// shared/src/oracle/news-policy-store.ts
// D1 access for `oracle_news_policy` (migration 0116) — the news resolver policy per SCOPE (#96).
// Design lock: docs/oracle-news-policy-schema-designlock-2026-07-21.md. Distinct from oracle_policy
// (price consensus/authoritative, keyed by asset_class): news ranking is a weighted score over tier
// + cross-vendor corroboration, plus a dedup threshold + per-consumer cap. Keyed by news scope with
// oracle_policy-style precedence (exact scope wins, else '*').
//
// Row/Zod/reader/writer live together and ship with the DDL in ONE commit (feedback_writer_ddl_lockstep).
// Uses the shared OracleD1 statement surface so the admin route can batch config-history rows
// atomically with the upsert (mirrors upsertPolicy / upsertRelationConfig).

import { z } from 'zod';
import type { OracleD1, OracleD1Stmt } from './oracle-store.js';

export type OracleNewsScope = '*' | 'macro' | 'ticker';

export interface OracleNewsPolicyRow {
  readonly scope:                   OracleNewsScope;
  readonly tier_a_weight:           number;   // ranking weight applied to a tier-A item
  readonly tier_b_weight:           number;
  readonly tier_c_weight:           number;
  readonly dedup_jaccard_threshold: number;   // (0,1] — was DEFAULT_JACCARD_THRESHOLD (dedup.ts)
  readonly max_items_per_consumer:  number;   // per-consumer digest cap (int)
  readonly cross_vendor_boost:      number;   // additive rank boost for a cross-vendor-corroborated item
  readonly policy_version:          string;
  readonly updated_at:              string;   // ISO-Z
  readonly updated_by:              string | null;
}

export const OracleNewsPolicyRowSchema = z.object({
  scope:                   z.enum(['*', 'macro', 'ticker']),
  tier_a_weight:           z.number().nonnegative(),
  tier_b_weight:           z.number().nonnegative(),
  tier_c_weight:           z.number().nonnegative(),
  dedup_jaccard_threshold: z.number().positive().max(1),
  max_items_per_consumer:  z.number().int().positive(),
  cross_vendor_boost:      z.number().nonnegative(),
  policy_version:          z.string().min(1),
  updated_at:              z.string().min(1),
  updated_by:              z.string().nullable(),
});

const COLS =
  'scope, tier_a_weight, tier_b_weight, tier_c_weight, dedup_jaccard_threshold, ' +
  'max_items_per_consumer, cross_vendor_boost, policy_version, updated_at, updated_by';

/** The news policy governing `scope`, MOST-SPECIFIC FIRST — an exact `scope` row wins, else the `'*'`
 *  base row (mirrors readPolicyRow's precedence). One query, ORDER BY, not two. null only if not even
 *  the '*' row exists (seed 0116 guarantees it). */
export async function readNewsPolicy(
  db: OracleD1,
  scope: OracleNewsScope = '*',
): Promise<OracleNewsPolicyRow | null> {
  return db
    .prepare(
      `SELECT ${COLS} FROM oracle_news_policy
        WHERE scope IN (?, '*')
        ORDER BY CASE WHEN scope = ? THEN 0 ELSE 1 END
        LIMIT 1`,
    )
    .bind(scope, scope)
    .first<OracleNewsPolicyRow>();
}

/** ALL news policy rows (every scope), for the dashboard CRUD table. Base '*' first, then the rest. */
export async function readAllNewsPolicies(db: OracleD1): Promise<OracleNewsPolicyRow[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM oracle_news_policy ORDER BY CASE WHEN scope = '*' THEN 0 ELSE 1 END, scope ASC`)
    .all<OracleNewsPolicyRow>();
  return res.results ?? [];
}

/** UPSERT one news policy row (full row). Extra statements (config-history) batch atomically with the
 *  write so the audit trail can never land without the row it describes (mirrors upsertPolicy). */
export async function upsertNewsPolicy(
  db: OracleD1,
  row: OracleNewsPolicyRow,
  extraStmts: OracleD1Stmt[] = [],
): Promise<void> {
  const upsert = db
    .prepare(
      `INSERT INTO oracle_news_policy (${COLS})
       VALUES (?,?,?,?,?,?,?,?,?,?)
       ON CONFLICT(scope) DO UPDATE SET
         tier_a_weight=excluded.tier_a_weight, tier_b_weight=excluded.tier_b_weight,
         tier_c_weight=excluded.tier_c_weight, dedup_jaccard_threshold=excluded.dedup_jaccard_threshold,
         max_items_per_consumer=excluded.max_items_per_consumer, cross_vendor_boost=excluded.cross_vendor_boost,
         policy_version=excluded.policy_version, updated_at=excluded.updated_at, updated_by=excluded.updated_by`,
    )
    .bind(
      row.scope, row.tier_a_weight, row.tier_b_weight, row.tier_c_weight, row.dedup_jaccard_threshold,
      row.max_items_per_consumer, row.cross_vendor_boost, row.policy_version, row.updated_at, row.updated_by,
    );
  await db.batch([upsert, ...extraStmts]);
}
